import type { OSGroup } from '#dn-types/downloads.ts';
import { osDisplayFromMetadata } from '#util/hostOs.ts';

/**
 * The IPv6-capable ping spelling for each OS. There is no universal one-liner: macOS `ping` is
 * IPv4-only (`ping6` is the only option there, and `-6` doesn't exist), Linux iputils `ping` is
 * dual-stack (its `ping6` is only a sometimes-present compat symlink), and Windows wants a flag.
 * Mobile has no terminal to run a command in.
 */
export function pingSpelling(os: OSGroup): string | null {
  switch (os) {
    case 'macOS':
      return 'ping6';
    case 'Windows':
      return 'ping -6';
    case 'Mobile':
      return null;
    case 'Linux':
    case 'Other':
      // 'Other' covers FreeBSD (dual-stack ping since 13) and Docker (Linux userland).
      return 'ping';
  }
}

export function pingCommand(os: OSGroup, target: string): string | null {
  const spelling = pingSpelling(os);
  return spelling === null ? null : `${spelling} ${target}`;
}

/**
 * Map a host's detected metadata.os onto the install picker's OS groups. The api's slug set is
 * open (zos, aix, ...): a reported-but-unlisted value is still a real OS with a shell, so it maps
 * to 'Other' (dual-stack plain `ping`). Null when the client has not reported an OS.
 */
export function osGroupFromMetadata(os: string | null | undefined): OSGroup | null {
  const display = osDisplayFromMetadata(os);
  return display === null ? null : (display.group ?? 'Other');
}

/**
 * Which host the done-step ping runs from by default: the first enrolled host, unless it's a phone
 * and the other host isn't — a phone has no terminal, so lead with the host that can actually run
 * the command.
 */
export function defaultPingSource(first: OSGroup, second: OSGroup): 0 | 1 {
  return first !== 'Mobile' || second === 'Mobile' ? 0 : 1;
}
